import { ChatDetailsResponse, ChatSettingsFormData } from '@choco-admin/types';
import { Card, CardBody } from '@heroui/react';
import React, { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FiSettings } from 'react-icons/fi';

import { FormFooter } from '../../../../../../components/FormFooter';
import {
  CHAT_TYPE_OPTIONS,
  getChatTypeOptionFromChatType,
} from '../../../../constants/chatOptions';
import { useChatPricesVisibility } from '../../../../hooks/useChatPricesVisibility';
import { useUpdateChatSettings } from '../../../../hooks/useUpdateChatSettings';
import { formatEmailsToString } from '../../../../utils/formHelpers';
import {
  ChatFormInputField,
  ChatTypeSelect,
  VisibleChatPricesSection,
} from '../../../shared';
import { PageHeader } from '../PageHeader';

import { SubCatalogSelect, DeliverySettingsSection } from './settings';

interface SettingsTabPanelProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const SettingsTabPanel: React.FC<SettingsTabPanelProps> = ({
  chatData,
  chatId,
}) => {
  // Initialize form with current chat data
  const formMethods = useForm({
    defaultValues: {
      chatName: chatData.chatName || '',
      restaurantName: chatData.restaurantName || '',
      deliveryAddress: chatData.address || '',
      additionalEmails: formatEmailsToString(chatData.additionalEmails),
      customerNumber: chatData.customerNumber || '',
      chatType:
        getChatTypeOptionFromChatType(chatData.type) || CHAT_TYPE_OPTIONS[0],
      subCatalog: [], // Will be populated by SubCatalogSelect
      createdContext: null, // Will be populated if needed
      visibleChatPrices: false,
      isPriceOverrideEnabled: false,
      hasCustomDeliverySettings: false,
      deliveryDays: {},
      cutoffTimes: {},
      cutoffDays: {},
      cutoffType: 'no_cutoff',
    },
  });

  // Get prices visibility
  const { pricesVisibility } = useChatPricesVisibility({
    chatId,
  });

  // Update form when prices visibility data is loaded
  useEffect(() => {
    if (!pricesVisibility) return;

    if (pricesVisibility.__typename === 'ChatPricesVisibilitySuccess') {
      // Enable price override by default when we have visibility data
      formMethods.setValue('isPriceOverrideEnabled', true, {
        shouldDirty: false,
      });
      formMethods.setValue(
        'visibleChatPrices',
        pricesVisibility.displayPricesOverride ?? false,
        { shouldDirty: false }
      );
    }
  }, [pricesVisibility, formMethods]);

  const { handleSubmit, formState } = formMethods;

  const { updateSettings, isUpdating } = useUpdateChatSettings({
    onSuccess: () => {
      formMethods.reset(formMethods.getValues());
    },
  });

  const handleSaveSettings = useCallback(
    async (data: ChatSettingsFormData) => {
      try {
        await updateSettings({
          chatId,
          chat: {
            chatName: data.chatName,
            restaurantName: data.restaurantName,
            deliveryAddress: data.deliveryAddress,
            additionalEmails: data.additionalEmails
              ?.split(',')
              .map((email: string) => email.trim()),
            customerNumber: data.customerNumber,
            subCatalogId: data.subCatalog?.[0]?.value,
          },
        });
      } catch (error) {
        // Error is handled by the hook
      }
    },
    [chatId, updateSettings]
  );

  const handleCancel = useCallback(() => {
    formMethods.reset();
  }, [formMethods]);

  const hasSupplier = !!chatData.supplierEntity;
  // Check if any supplier user requires customer number
  const supplierRequiresCustomerNumber =
    chatData.users?.some(
      (user) => user.supplier && user.orderPreferences?.whatsApp
    ) ?? false;

  return (
    <div className="space-y-6">
      <Card>
        <CardBody className="space-y-8">
          <PageHeader
            title="Settings"
            subtitle="Configure chat details, delivery settings, and preferences."
            icon={<FiSettings className="w-6 h-6 text-primary" />}
          />

          <FormProvider {...formMethods}>
            <form
              onSubmit={handleSubmit(handleSaveSettings)}
              className="space-y-6"
            >
              {/* Basic Chat Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ChatFormInputField name="chatName" isRequired />
                  <ChatFormInputField name="restaurantName" />
                </div>

                <ChatFormInputField name="deliveryAddress" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ChatFormInputField name="additionalEmails" type="email" />
                  <ChatFormInputField
                    name="customerNumber"
                    customerNumberMandatory={supplierRequiresCustomerNumber}
                  />
                </div>
              </div>

              {/* Chat Configuration */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Configuration
                </h3>

                <ChatTypeSelect disabled />

                <SubCatalogSelect />

                <VisibleChatPricesSection />
                {/* TODO: Add CustomerLevelMov component */}
                {/* TODO: Add ChatCreatedContextField when marketplace feature is enabled */}
              </div>

              {/* Delivery Settings */}
              {!hasSupplier && (
                <Card className="border-warning-200 bg-warning-50">
                  <CardBody>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-warning-800">
                        Delivery days and cutoff times
                      </h4>
                      <p className="text-sm text-warning-700">
                        Add a supplier entity to this chat in order to define
                        chat specific delivery days and cutoff times.
                      </p>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Delivery Settings */}
              {hasSupplier && <DeliverySettingsSection chatId={chatId} />}

              {/* Form Actions */}
              <FormFooter
                onCancel={handleCancel}
                onSave={handleSubmit(handleSaveSettings)}
                isSaveLoading={isUpdating}
                isSaveDisabled={!formState.isDirty}
              />
            </form>
          </FormProvider>
        </CardBody>
      </Card>
    </div>
  );
};
